import { useEffect, type FC } from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { Error500 } from 'shared/page/Error500'

import { Actions as UserProfileActions } from 'lmcv/redux/modules/userProfile'
import { Actions as LoyaltyProfileActions } from 'lmcv/redux/modules/loyaltyProfile'
import { Actions as NotificationActions } from 'lmcv/redux/modules/notification'
import { AuthLayout } from 'lmcv/containers/AuthLayout'
import { PrivateRoute, GuestRoute, NoMatch, getRoutes } from 'lmcv/routes'
import { AuthLayoutShell } from 'lmcv/components'

import { useLysUser } from './hooks'
import { getLysRoutes } from './routes/lysRoutes'

type AppProps = {
  onLoyaltyProgram?: string
  loyaltyProRelationship?: string
  mdmClientNumber?: number | null
  loyaltyProgramCode?: number | string | Record<string, unknown> | null
  userOccupation?: string
  lysHomepageEnabled: boolean
  loyaltyArchitectsEnabled?: boolean
  newFaqEnabled?: boolean
}

type LoyaltyProfile = {
  hasArchitectCashback?: boolean
  status?: number
} | null

type RootState = {
  userProfile: {
    get: {
      isRequest: boolean
    }
  }
  loyaltyProfile: {
    get: {
      payload: LoyaltyProfile
      isRequest: boolean
    }
  }
}

const App: FC<AppProps> = ({
  onLoyaltyProgram,
  loyaltyProRelationship,
  mdmClientNumber,
  loyaltyProgramCode,
  userOccupation,
  lysHomepageEnabled,
  loyaltyArchitectsEnabled,
  newFaqEnabled
}) => {
  localStorage.setItem('@loyalty:token', onLoyaltyProgram ?? '')
  localStorage.setItem('@loyalty:proRelationship', loyaltyProRelationship ?? '')

  const dispatch = useDispatch()

  const { isRequest: isRequestUser } = useSelector(
    (state: RootState) => state.userProfile.get
  )

  const { payload: loyaltyProfile, isRequest: isRequestLoyalty } = useSelector(
    (state: RootState) => state.loyaltyProfile.get
  )

  const { isCheckingLys, isLysUser, lysProgramType } = useLysUser({
    mdmClientNumber,
    lysHomepageEnabled
  })

  useEffect(() => {
    dispatch(UserProfileActions.getProfile())
    dispatch(LoyaltyProfileActions.get())
  }, [dispatch])

  useEffect(() => {
    if (!isRequestLoyalty) {
      dispatch(NotificationActions.get())
    }
  }, [dispatch, isRequestLoyalty])

  if (isRequestUser || isRequestLoyalty || isCheckingLys) {
    return <AuthLayoutShell />
  }

  if (!loyaltyProfile) {
    return <Error500 />
  }

  const cashbackEnabled =
    loyaltyProfile.hasArchitectCashback || loyaltyProfile.status === 7

  const effectiveProgramType = isLysUser
    ? (lysProgramType ?? loyaltyProgramCode)
    : loyaltyProgramCode

  const routes = [
    ...(isLysUser ? getLysRoutes() : []),
    ...getRoutes({
      cashbackEnabled,
      isLysUser,
      programType: effectiveProgramType,
      userOccupation,
      loyaltyArchitectsEnabled,
      newFaqEnabled
    })
  ]

  return (
    <BrowserRouter basename="/fidelidade">
      <AuthLayout
        mdmClientNumber={mdmClientNumber ?? undefined}
        isLysUser={isLysUser}
        programType={effectiveProgramType as number}
        userOccupation={userOccupation}
        lysHomepageEnabled={lysHomepageEnabled}>
        <Switch>
          {routes.map(({ isPrivate, ...props }, index) =>
            isPrivate ? (
              <PrivateRoute
                key={`private-route-${index}`}
                {...props}
                mdmClientNumber={mdmClientNumber}
                lysHomepageEnabled={lysHomepageEnabled}
              />
            ) : (
              <GuestRoute key={`guest-route-${index}`} {...props} />
            )
          )}
          <Route component={NoMatch} />
        </Switch>
      </AuthLayout>
    </BrowserRouter>
  )
}

export default App
